import {
  IGNORE_PREFIXES,
  IGNORE_EXTENSIONS,
  ABOUT,
  SUPPORTED_LOCALES,
  MAIN_SITE_ROUTES,
} from "./constants";
import type { LocalePathInfo } from "./types";

/**
 * 路径处理工具类
 */
export class PathHandler {
  private static readonly ARTICLE_DEVICE_MARKETS = ["us", "hk", "token"];

  /**
   * 是否命中 app 侧 article market 路由（/article/us|hk|token）
   * 支持带 locale 前缀场景：/:locale/article/:market
   */
  private static isArticleMarketRoute(segments: string[]): boolean {
    const routeStartIndex = this.isValidLocale(segments[0]) ? 1 : 0;
    const route = segments[routeStartIndex];
    const market = segments[routeStartIndex + 1];

    if (route !== "article" || !market) {
      return false;
    }

    return this.ARTICLE_DEVICE_MARKETS.includes(market.toLowerCase());
  }

  /**
   * 检查是否为静态资源或API路径
   */
  static isStaticOrApi(pathname: string): boolean {
    // 前缀判断
    if (IGNORE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
      return true;
    }
    // 后缀判断
    if (IGNORE_EXTENSIONS.some((ext) => pathname.endsWith(ext))) {
      return true;
    }
    return false;
  }

  /**
   * 检查是否为有效的语言代码
   */
  static isValidLocale(locale: string | undefined): locale is string {
    return Boolean(locale && SUPPORTED_LOCALES.includes(locale as any));
  }

  /**
   * 检查路径是否需要特殊英语处理（英语不显示locale前缀）
   */
  static shouldUseEnglishPath(locale: string): boolean {
    return locale === "en";
  }

  /**
   * 获取路径中的locale信息
   *
   * ABOUT 为官网路由但是 local 在 ABOUT 后面
   * "article"、"author"、"pricing"、"sitemap" 也是官网路由，locale 在路由前面：
   * 例如：/zh/article、/zh/pricing
   */
  static getLocaleFromPath(segments: string[]): LocalePathInfo {
    const isAboutSite = segments[0] === ABOUT;
    const isArticleMarketRoute = this.isArticleMarketRoute(segments);

    // 用于 device 路由：主站相关页面（/about/*、/blog、/features 等）都应跳过 device 改写
    // 例外：/article/us|hk|token 需要进入 app 路由，走 device 动态注入
    const isMainSite =
      !isArticleMarketRoute &&
      (isAboutSite ||
        MAIN_SITE_ROUTES.includes(segments[0]) ||
        MAIN_SITE_ROUTES.includes(segments[1]));

    // 只有 /about/* 的 locale 在 /about 后面；其他路由（含 /blog、/features）locale 都在最前面
    const localeIndex = isAboutSite ? 1 : 0;
    const locale = segments[localeIndex];

    return {
      locale: this.isValidLocale(locale) ? locale : null,
      isMainSite,
      isAboutSite,
      localeIndex,
    };
  }

  /**
   * 构建目标路径
   */
  static buildTargetPath(
    segments: string[],
    locale: string,
    isAboutSite: boolean = false,
  ): string {
    if (this.shouldUseEnglishPath(locale)) {
      // 英语不显示locale前缀
      if (isAboutSite) {
        return `/${ABOUT}${segments.length ? "/" + segments.join("/") : ""}`;
      }
      return `/${segments.join("/")}`;
    }

    // 其他语言显示locale前缀
    if (isAboutSite) {
      return `/${ABOUT}/${locale}${segments.length ? "/" + segments.join("/") : ""}`;
    }
    if (segments.length === 0) {
      return `/${locale}`;
    }
    return `/${locale}/${segments.join("/")}`;
  }

  /**
   * 拼接路径段
   *
   * @param segments - 路径段数组
   * @param prefix - 可选的路径前缀（默认："/"）
   * @returns 拼接后的路径字符串
   */
  static joinSegments(segments: string[], prefix: string = "/"): string {
    if (segments.length === 0) {
      return prefix === "/" ? "/" : prefix;
    }
    const joined = segments.join("/");
    return prefix === "/" ? `/${joined}` : `${prefix}/${joined}`;
  }

  /**
   * 移除路径中的 locale 前缀
   *
   * 注意：此方法使用路径段分割的方式，对于 /zh-tw/xxx 这样的路径，
   * 第一个段是 "zh-tw"，不会被误判为 "zh"，因为 isValidLocale 会检查
   * 完整的段是否在 SUPPORTED_LOCALES 中。
   *
   * @param pathname - 完整路径（可能包含 locale 前缀）
   * @returns 移除 locale 前缀后的路径
   */
  static removeLocalePrefix(pathname: string): string {
    const segments = pathname.split("/").filter(Boolean);

    // 检查第一个路径段是否是 locale
    // 对于 /zh-tw/xxx，第一个段是 "zh-tw"，isValidLocale 会检查完整的段
    if (segments.length > 0 && this.isValidLocale(segments[0])) {
      // 移除 locale 前缀
      return "/" + segments.slice(1).join("/");
    }

    // 如果没有 locale 前缀，直接返回原路径
    return pathname;
  }

  static readonly DEVICE_PREFIXES = [
    "desktop",
    "ios",
    "android",
    "h5",
  ] as const;

  /**
   * 移除路径首段的 device 前缀（如 /desktop/login → /login）
   */
  static removeDevicePrefix(pathname: string): string {
    const segments = pathname.split("/").filter(Boolean);
    if (
      segments.length > 0 &&
      this.DEVICE_PREFIXES.includes(
        segments[0] as (typeof PathHandler.DEVICE_PREFIXES)[number],
      )
    ) {
      const rest = segments.slice(1).join("/");
      return rest ? `/${rest}` : "/";
    }
    return pathname;
  }

  static isRootPath(pathname: string): boolean {
    const originalPath = this.removeLocalePrefix(pathname);
    return originalPath === "/";
  }

  /** 方案4：产品域 canonical 官网路径（无 /about 前缀） */
  static isMainSiteSeoPath(pathname: string): boolean {
    const segments = pathname.split("/").filter(Boolean);
    const routeStartIndex =
      segments.length > 0 && this.isValidLocale(segments[0]) ? 1 : 0;

    if (routeStartIndex === 0 && segments.length === 0) {
      return true;
    }

    const route = segments[routeStartIndex];
    if (!route) {
      return routeStartIndex > 0;
    }

    return MAIN_SITE_ROUTES.includes(route);
  }

  /**
   * 英文 + 无 URL locale 前缀的官网 SEO 路径：交给 beforeFiles rewrite，避免 307 到 /about/*
   */
  static shouldServeMainSiteViaRewrite(
    pathname: string,
    cookieLocale: string | undefined,
    pathLocale: string | null,
  ): boolean {
    if (!this.isMainSiteSeoPath(pathname) || pathLocale) {
      return false;
    }

    const locale = cookieLocale || "en";
    return this.shouldUseEnglishPath(locale);
  }

  /** legacy /about/en/* → 方案4 SEO 路径 */
  static mapLegacyAboutEnToSeoPath(segments: string[]): string {
    const restPath = this.joinSegments(segments.slice(2));
    return restPath === "/" ? "/" : restPath;
  }

  /**
   * 构建带 locale 的路径
   * 根据 locale 添加或移除语言前缀
   *
   * @param basePath - 基础路径（可能已包含 locale 前缀）
   * @param locale - 目标 locale
   * @returns 带 locale 的完整路径
   */
  static buildPathWithLocale(basePath: string, locale: string): string {
    // 移除路径中可能存在的旧 locale 前缀
    let pathWithoutLocale = this.removeLocalePrefix(basePath);

    // 确保路径以 / 开头
    if (!pathWithoutLocale.startsWith("/")) {
      pathWithoutLocale = "/" + pathWithoutLocale;
    }

    if (this.shouldUseEnglishPath(locale)) {
      // 英语不显示 locale 前缀
      return pathWithoutLocale;
    } else {
      // 其他语言显示 locale 前缀
      // 格式：/${locale}/path
      // 处理根路径特殊情况
      if (pathWithoutLocale === "/") {
        return `/${locale}`;
      }
      return `/${locale}${pathWithoutLocale}`;
    }
  }
}
